// src/components/propertyPanel/CommonProperties/RichEditorControl.js
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { COMPONENT_TYPES } from '../../../constants';
import { usePropertyPanel } from '../PropertyPanelContext';
import RichEditorModal from '../../common/RichEditorModal';
import { yooptaToBackend, backendToYoopta, validateBackendBlocks } from '../../../utils/YooptaDataConverter';

// props를 추가하여 외부에서 초기값을 전달받을 수 있도록 함
const RichEditorControl = ({ initialContent, onContentSave, showPreview = true }) => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // 로컬 상태 관리
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorContent, setEditorContent] = useState(initialContent || '');
  
  // 컨텍스트 사용 (옵셔널)
  const context = usePropertyPanel() || {};
  const {
    devicePropertyTab = 'pc'
  } = context;
  
  // 리치 에디터 저장 처리 (HTML과 블럭 데이터 모두 처리)
  const handleEditorSave = (content, blocks) => {
    if (!selectedComponent) return;
    
    // 🔢 블록 순서 정보 추가 로깅
    const blockOrderInfo = blocks ? Object.values(blocks).map(block => ({
      id: block.id,
      type: block.type,
      order: block.meta?.order || 0
    })).sort((a, b) => a.order - b.order) : null;
    

    // 기존 콘텐츠 타입 확인 - Pure HTML로 저장된 콘텐츠인지 체크
    const currentContent = selectedComponent.content;
    let wasPureHtml = false;
    
    if (currentContent && typeof currentContent === 'object') {
      if (currentContent.editorType === 'quill' || !currentContent.originalBlock) {
        wasPureHtml = true;
      } else if (currentContent.deviceProperty) {
        const deviceContent = currentContent.deviceProperty[devicePropertyTab];
        if (deviceContent && (deviceContent.editorType === 'quill' || !deviceContent.originalBlock)) {
          wasPureHtml = true;
        }
      }
    }
    
    // 외부에서 제공한 저장 함수가 있으면 사용
    if (typeof onContentSave === 'function') {
      onContentSave(content, blocks);
    } else {
      // 웹빌더: JSON에 HTML과 블럭 데이터 모두 저장
      const backendBlocks = blocks ? yooptaToBackend(blocks) : null;
      
      // Pure HTML로 저장된 콘텐츠를 Yoopta로 편집한 경우 CSS 보존
      let finalEditorType = blocks ? "yoopta" : "quill";
      if (wasPureHtml && blocks) {
        // Pure HTML → Yoopta 변환 시 editorType을 'mixed'로 표시
        finalEditorType = "mixed";
      }
      
      // CSS 추출 및 별도 저장
      let cssStyles = '';
      let htmlWithoutStyle = content;
      
      if (content && content.includes('<style>')) {
        const styleMatches = content.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
        if (styleMatches) {
          cssStyles = styleMatches.map(match => {
            const styleContent = match.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
            return styleContent ? styleContent[1] : '';
          }).join('\n');
          
          // HTML에서 style 태그 제거
          htmlWithoutStyle = content.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').trim();
        }
      }
      
      // 기존 CSS가 있다면 보존
      if (!cssStyles && currentContent) {
        if (typeof currentContent === 'object') {
          cssStyles = currentContent.cssStyles || '';
          if (!cssStyles && currentContent.deviceProperty) {
            const deviceContent = currentContent.deviceProperty[devicePropertyTab];
            cssStyles = deviceContent?.cssStyles || '';
          }
        }
      }
      
      const contentData = {
        html: htmlWithoutStyle,
        originalBlock: backendBlocks,
        editorType: finalEditorType,
        wasPureHtml: wasPureHtml,
        cssStyles: cssStyles // CSS를 별도 필드로 저장
      };
      

      // 없으면 기본 저장 로직 사용
      if (selectedComponent.type === COMPONENT_TYPES.RICH_EDITOR) {
        dispatch(updateComponent({
          id: selectedComponent.id,
          changes: { content: contentData }
        }));
      } else if (devicePropertyTab) {
        // 디바이스별 속성 처리
        const existingContent = selectedComponent.content ? 
          JSON.parse(JSON.stringify(selectedComponent.content)) : 
          { deviceProperty: { pc: {}, mobile: {} } };
        
        if (!existingContent.deviceProperty) {
          existingContent.deviceProperty = { pc: {}, mobile: {} };
        }
        
        existingContent.deviceProperty[devicePropertyTab] = contentData;
        
        if (devicePropertyTab === 'pc') {
          existingContent.html = content; // 호환성을 위해 html 필드도 유지
        }
        
        dispatch(updateComponent({
          id: selectedComponent.id,
          changes: { content: existingContent }
        }));
      } else {
        dispatch(updateComponent({
          id: selectedComponent.id,
          changes: { content: contentData }
        }));
      }
    }
    
    // 모달 닫기
    setIsEditorOpen(false);
  };
  
  // 에디터 열기 함수 - 외부에서 호출할 수 있게 export (현재 미사용)
  // const openEditor = (content) => {
  //   setEditorContent(content || editorContent);
  //   setIsEditorOpen(true);
  // };
  

  // 에디터 초기 데이터 가져오기 (블럭 우선, 없으면 HTML)
  const getEditorInitialData = () => {

    if (!selectedComponent) {
      return '';
    }
    
    let contentSource = null;
    
    if (typeof selectedComponent.content === 'object' && selectedComponent.content !== null) {
      if (selectedComponent.content.deviceProperty) {
        contentSource = selectedComponent.content.deviceProperty[devicePropertyTab];
      } else {
        contentSource = selectedComponent.content;
      }
    } else {
      return selectedComponent.content || '';
    }
    

    // originalBlock이 있으면 우선 사용 (편집 시 정확한 블록 구조 유지)
    if (contentSource?.originalBlock && validateBackendBlocks(contentSource.originalBlock)) {
      const yooptaBlocks = backendToYoopta(contentSource.originalBlock);
      if (yooptaBlocks && Object.keys(yooptaBlocks).length > 0) {
        // 🔢 복원된 블록 순서 정보 로깅
    const restoredBlockOrderInfo = yooptaBlocks ? Object.values(yooptaBlocks).map(block => ({
      id: block.id,
      type: block.type,
      order: block.meta?.order || 0
    })).sort((a, b) => a.order - b.order) : null;
    
        return yooptaBlocks;
      } else {
      }
    }
    
    // originalBlock이 없거나 변환 실패 시 HTML 사용
    let htmlContent = contentSource?.html || contentSource || '';
    
    // CSS가 별도로 저장되어 있으면 합치기
    if (contentSource?.cssStyles) {
      htmlContent = `<style>\n${contentSource.cssStyles}\n</style>\n\n${htmlContent}`;
    }
    

    // HTML 정리: body 태그와 불필요한 속성 제거
    if (typeof htmlContent === 'string' && htmlContent.includes('<body')) {
      // body 태그 내용만 추출
      const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (bodyMatch) {
        htmlContent = bodyMatch[1].trim();
      }
      
      // yoopta-clipboard 관련 속성들 정리
      htmlContent = htmlContent.replace(/data-editor-id="[^"]*"/g, '');
      htmlContent = htmlContent.replace(/data-meta-[^=]*="[^"]*"/g, '');
      htmlContent = htmlContent.replace(/\s+/g, ' ').trim();
    }
    
    return htmlContent;
  };
  
  // 리치 에디터 버튼 및 미리보기 렌더링
  const renderEditorButton = () => (
    <div className="property-group">
      <div className="property-group-title">
        <i className="fas fa-font"></i> 텍스트 내용
      </div>
      <button 
        onClick={() => {
         
          
          let currentContent = '';
          
          // 초기 컨텐츠 설정
          if (initialContent) {
           
            currentContent = initialContent;
          } else {
           
            currentContent = getEditorInitialData();
          }
          
         
          
         
          setEditorContent(currentContent);
         
          setIsEditorOpen(true);
        }}
        style={{ 
          padding: '8px 12px', 
          backgroundColor: '#4a90e2', 
          color: 'white', 
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '10px',
          border: 'none',
          width: '100%'
        }}
      >
        에디터로 편집하기
      </button>
    </div>
  );

  // 메인 렌더링
  return (
    <>
      {renderEditorButton()}
      
      {/* 리치 에디터 모달 */}
      {isEditorOpen && (
        <RichEditorModal
          isOpen={isEditorOpen}
          content={editorContent}
          initialContent={editorContent}
          onSave={handleEditorSave}
          onClose={() => {
           
            setIsEditorOpen(false);
          }}
        />
      )}
    </>
  );
};

// openEditor 함수를 노출하기 위한 ref 패턴 제공
export const withRichEditorRef = (Component) => {
  return React.forwardRef((props, ref) => {
    const richEditorRef = React.useRef({
      openEditor: () => {}
    });
    
    return (
      <Component 
        {...props} 
        richEditorRef={richEditorRef} 
      />
    );
  });
};

export default RichEditorControl;